import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const HospitalityContext = createContext();
export const useHospitalityData = () => useContext(HospitalityContext);

export const HospitalityProvider = ({ children }) => {
  const [hospitalityData, setHospitalityData] = useState([]);
  const [reservationLoading, setReservationLoading] = useState(false);
  const [loading, setLoading] = useState(false); // global data loading

  // Global Data States
  const [weekStart, setWeekStart] = useState("Monday");
  const [weekEnd, setWeekEnd] = useState("Saturday");
  const [weekends, setWeekend] = useState([0]);
  const [additionalOffDays, setAdditionalOffDays] = useState({});
  const [holidays, setHolidays] = useState([]);
  const [workingHours, setWorkingHours] = useState(9);
  const [relaxation, setRelaxation] = useState(null);
  const [maxRelaxation, setMaxRelaxation] = useState(null);
  const [timezone, setTimezone] = useState(null);
  const [currency, setCurrency] = useState(null);
  const [dateformat, setDateformat] = useState(null);
  const [businessDate, setBusinessDate] = useState(null);
  const [company, setCompanyName] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL;

  // Helper: check if token exists
  const getToken = () => localStorage.getItem("token");

  // Fetch reservations only if logged in
  const fetchReservations = async (token) => {
    if (!token) return; // prevent any call without token

    setReservationLoading(true);
    try {
      const response = await fetch(`${apiUrl}/booking/reservations?islist=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const result = await response.json();
      if (result.success) {
        const mappedData = result.reservations.map((r) => ({
          guestName: r.guest_name || "Unknown",
          pax: (r.num_adults || 0) + (r.num_children || 0),
          resNo: r.booking_number || "N/A",
          expectedTime: r.expectedTime || "N/A",
          checkinDate: r.check_in_date || "N/A",
          checkoutDate: r.check_out_date || "N/A",
          bookingStatus: r.booking_status || "Unknown",
          phone: r.guest_phone || "N/A",
          email: r.guest_email || "N/A",
          roomNumbers: r.room_numbers || "N/A",
          mealPlan: r.meal_plan || "N/A",
          roomTypes: r.room_types || "N/A",
          bookingSource: r.booking_source || "N/A",
          bookingID: r.booking_id || "N/A",
        }));
        setHospitalityData(mappedData);
      } else {
        console.warn("Failed to fetch reservations:", result);
      }
    } catch (error) {
      console.error("Error fetching reservations:", error);
    } finally {
      setReservationLoading(false);
    }
  };

  // Fetch global data only if logged in
  const fetchGlobalData = async (token) => {
    if (!token) return;

    setLoading(true);
    try {
      const { data } = await axios.get(`${apiUrl}/company/global-data`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setWeekStart(data.week_start || "Monday");
      setWeekEnd(data.week_end || "Saturday");
      setWeekend(data.weekend || [0]);
      setAdditionalOffDays(data.additional_off_days || {});
      setHolidays((data.holidays || []).map((h) => h.date));
      setWorkingHours(data.working_hours || 9);
      setRelaxation(data.relaxation);
      setMaxRelaxation(data.max_relaxation);
      setTimezone(data.timezone);
      setCurrency(data.currency);
      setDateformat(data.dateformat);
      setBusinessDate(data.business_date);
      setCompanyName(data.company);
      localStorage.setItem("business_date", data.business_date);
    } catch (error) {
      console.error("Error fetching global data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Master effect — only run APIs if token exists
  useEffect(() => {
    const token = getToken();

    if (token) {
      fetchGlobalData(token);
      fetchReservations(token);
    } else {
      // Optional: clear data if no token (like logout)
      setHospitalityData([]);
      setCompanyName(null);
    }
  }, [apiUrl]); // run once per API base URL or when token changes externally

  return (
    <HospitalityContext.Provider
      value={{
        hospitalityData,
        setHospitalityData,
        reservationLoading,
        loading,
        weekStart,
        weekEnd,
        weekends,
        additionalOffDays,
        holidays,
        workingHours,
        relaxation,
        maxRelaxation,
        timezone,
        currency,
        dateformat,
        businessDate,
        company,
      }}
    >
      {children}
    </HospitalityContext.Provider>
  );
};
